import { usePaginate } from "~/components/pagination/usePaginate";
import {
  CasesOrder,
  CasesOrderField,
  CaseStatus,
  OrderDirection,
  useGetCasesQuery,
} from "~/operations";
import { useFeatureFlags } from "~/providers/FeatureFlagsProvider";
import { Header } from "~/types/table";
import { ScopeType, SpaceOrWorkspaceScope } from "~/hooks/useScope";
import { useSort } from "~/pages/inventory/hooks/useSort";

export const useTopActionsProgressCard = (scope: SpaceOrWorkspaceScope) => {
  const paginate = usePaginate({ defaultPageSize: 5, prefix: "progress" });
  const { isFeatureEnabled } = useFeatureFlags();
  const isMoveTheNeedleFeatureEnabled = isFeatureEnabled("Move the needle");

  const { orderBy, handleSortClick } = useSort<CasesOrder>({
    defaultSort: {
      field: CasesOrderField.SpaceRiskDelta,
      direction: OrderDirection.Desc,
    },
    validFields: [CasesOrderField.SpaceRiskDelta, CasesOrderField.CreatedAt],
  });

  const {
    data: openTicketsData,
    loading: openTicketsDataLoading,
    refetch,
  } = useGetCasesQuery({
    variables: {
      ...paginate.pageVars,
      input: {
        scopeMrn: scope.mrn,
        statuses: [CaseStatus.Open],
        orderBy,
      },
    },
    skip: !isMoveTheNeedleFeatureEnabled,
  });

  const tickets = (openTicketsData?.cases?.edges || []).flatMap((ticketEdge) =>
    ticketEdge.__typename === "CaseEdge" ? ticketEdge.node : [],
  );
  const ticketsTotalCount = openTicketsData?.cases?.totalCount || 0;
  const ticketsPageInfo = openTicketsData?.cases?.pageInfo;
  const paginateProps = paginate.pageProps(ticketsPageInfo);
  const isEmpty = ticketsTotalCount === 0 && !openTicketsDataLoading;

  const tableHeaders: Header[] = [
    {
      id: "TICKET",
      label: "Ticket",
      options: {
        width: "40%",
      },
    },
    {
      id: "CREATED_AT",
      label: "Date Created",
      options: {
        width: "25%",
      },
      sortable: true,
    },
    {
      id: "PROGRESS",
      label: "Progress",
    },
    {
      id: "SPACE_RISK_DELTA",
      label:
        scope.type === ScopeType.Workspace
          ? "Δ Workspace Risk"
          : "Δ Space Risk",
      sortable: true,
    },
    {
      id: "CREATED_BY",
      label: "Created By",
    },
  ];

  return {
    tickets,
    paginateProps,
    loading: openTicketsDataLoading,
    refetch,
    tableHeaders,
    isEmpty,
    handleSortClick,
    orderBy,
  };
};
